import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import useFetch from '../../../hooks/useFetch';
import { fetchUserTeams, fetchEloTeams } from '../../../api/userTeams';
import { fetchUserGames } from '../../../api/games';
import { fetchUserCardCount } from '../../../api/cards';
import { filterTempTeams } from '../../../helpers/teamHelpers';

const StatsStrip = () => {
    const { data: rawTeams } = useFetch(fetchUserTeams, []);
    const { data: rawGames } = useFetch(fetchUserGames, []);
    const { data: rawLeaderboard } = useFetch(fetchEloTeams, []);
    const { data: cardData } = useFetch(fetchUserCardCount, { totalCards: 0 });

    const teams = useMemo(() => filterTempTeams(rawTeams || []), [rawTeams]);
    const cardCount = cardData?.totalCards || 0;

    const record = useMemo(() => {
        const allFinal = (rawGames || []).filter(g => g.isFinal);
        return allFinal.reduce(
            (acc, g) =>
                g.resultDescription?.startsWith('Win')
                    ? { ...acc, w: acc.w + 1 }
                    : { ...acc, l: acc.l + 1 },
            { w: 0, l: 0 }
        );
    }, [rawGames]);

    const leaderboard = useMemo(() => {
        const sorted = (rawLeaderboard || []).sort((a, b) => (b.elo || 0) - (a.elo || 0));
        return filterTempTeams(sorted);
    }, [rawLeaderboard]);

    const bestTeam = teams.length
        ? teams.reduce((best, t) => ((t.score || 0) > (best.score || 0) ? t : best), teams[0])
        : null;

    const userRank = bestTeam
        ? leaderboard.findIndex(t => String(t.userTeamID) === String(bestTeam.userTeamID)) + 1
        : 0;

    const ctaLinks = useMemo(() => {
        if (cardCount === 0) return [{ label: 'Open Your First Pack', to: '/packs/buy' }];
        if (teams.length === 0) return [{ label: 'Build Your First Team', to: '/team/create' }];
        const recentGames = (rawGames || []).filter(g => g.isFinal);
        if (recentGames.length === 0) return [{ label: 'Play Your First Game', to: '/campaigns' }];
        return [{ label: 'My Teams', to: '/team/user' }];
    }, [cardCount, teams.length, rawGames]);

    return (
        <div className="dash-strip">
            <div className="dash-strip-info">
                {bestTeam ? (
                    <Link to="/team/user" className="dash-team-name">
                        {bestTeam.name}
                    </Link>
                ) : (
                    <span className="dash-team-name">Welcome!</span>
                )}
                {bestTeam?.elo != null && (
                    <div className="dash-stat">
                        <span className="dash-stat-value">{bestTeam.elo}</span>
                        <span className="dash-stat-label">ELO</span>
                    </div>
                )}
                <div className="dash-stat">
                    <span className="dash-stat-value">
                        {record.w}-{record.l}
                    </span>
                    <span className="dash-stat-label">W-L</span>
                </div>
                {userRank > 0 && (
                    <div className="dash-stat">
                        <span className="dash-stat-value">#{userRank}</span>
                        <span className="dash-stat-label">Rank</span>
                    </div>
                )}
            </div>
            <div className="dash-cta-group">
                {ctaLinks.map(link => (
                    <Link key={link.to} to={link.to} className="dash-cta">
                        {link.label}
                    </Link>
                ))}
            </div>
        </div>
    );
};

export default StatsStrip;
